import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const KeepsakesCollectionModal = ({ pet, onClose, onUpdate }) => {
  const [keepsakes, setKeepsakes] = useState([]);
  const [staffName, setStaffName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKeepsakesStatus();
  }, []);

  const fetchKeepsakesStatus = async () => {
    try {
      const response = await axios.get(`${API}/pets/${pet.id}/keepsakes-status`);
      setKeepsakes(response.data.keepsakes);
    } catch (error) {
      console.error('Error fetching keepsakes status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleKeepsake = async (keepsake) => {
    const newCollectedState = !keepsake.collected;
    
    try {
      await axios.post(`${API}/pets/${pet.id}/collect-keepsake`, null, {
        params: {
          keepsake_name: keepsake.keepsake_name,
          collected: newCollectedState,
          staff_name: staffName || undefined
        }
      });
      
      // Refresh keepsakes status
      await fetchKeepsakesStatus();
      onUpdate();
    } catch (error) {
      console.error('Error toggling keepsake:', error);
      alert('Failed to update keepsake status');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const collectedCount = keepsakes.filter(k => k.collected).length;
  const totalCount = keepsakes.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">📦 Keepsakes Collection</h3>
            <p className="text-sm text-gray-600">{pet.pet_name} ({pet.client_name})</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span className="font-semibold">{collectedCount} of {totalCount} collected</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${collectedCount === totalCount ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${totalCount > 0 ? (collectedCount / totalCount) * 100 : 0}%` }}
            ></div>
          </div>
        </div>

        {/* Staff Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Name (optional)
          </label>
          <input
            type="text"
            value={staffName}
            onChange={(e) => setStaffName(e.target.value)}
            placeholder="Enter your name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Keepsakes List - Large Tap Cards */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : keepsakes.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No keepsakes for this pet
            </div>
          ) : (
            keepsakes.map((keepsake, index) => (
              <button
                key={index}
                onClick={() => handleToggleKeepsake(keepsake)}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  keepsake.collected
                    ? 'bg-green-50 border-green-400 hover:bg-green-100'
                    : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className={`text-2xl mr-2 ${keepsake.collected ? 'text-green-600' : 'text-gray-400'}`}>
                        {keepsake.collected ? '✅' : '☐'}
                      </span>
                      <h4 className={`font-medium ${keepsake.collected ? 'text-green-900' : 'text-gray-900'}`}>
                        {keepsake.keepsake_name}
                      </h4>
                    </div>
                    {keepsake.collected && (
                      <div className="ml-10 mt-1">
                        <div className="text-xs text-green-700">
                          <p>✓ Collected by {keepsake.collected_by || 'Unknown'}</p>
                          <p>{formatDateTime(keepsake.collected_at)}</p>
                        </div>
                        <p className="text-xs text-green-600 mt-1 italic">Tap to undo if collected by mistake</p>
                      </div>
                    )}
                    {!keepsake.collected && (
                      <p className="ml-10 mt-1 text-xs text-gray-500">Tap to mark as collected</p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// SMS Preview Modal Component

export default KeepsakesCollectionModal;
